import { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import Peer from "simple-peer";
import "./../../App.css";
import PairingForm from "./PairingForm";
import { createRTCClient, getMyVideoStreamTrack } from "../RtcClientSdk";
import { useApiResponse } from "../../context/ApiResponseContext";
import Alert from "../../layout/Alert";
import Loading from "../../layout/Loading";
import isMobileDevice from "../../utils/isMobileDevice";
import showHomeOnboardingTour from "../../layout/PcOnboarding";
import * as authService from "../../actions/auth.actions";
import {
  UI_VERSION,
  AUTH_CHECKING_MESSAGE,
  INFORMATION_PAGE,
  SUBSCRIPTION_PAGE,
  VIEWER_LANDING_PAGE,
} from "../../Constants";

function Home({ isAuthenticated, subscriptionType, uiOnboardingLevel }) {
  const [me, setMe] = useState("");
  const [stream, setStream] = useState();
  const [enableMultiThreadedStream, setEnableMultiThreadedStream] =
    useState(false);
  const [callAccepted, setCallAccepted] = useState(false);
  const [viewersList, setViewersList] = useState(["", "", ""]);
  const clientSocketRef = useRef();
  const connectionRef = useRef("");
  const [apiResponse, setApiResponse] = useApiResponse();

  useEffect(() => {
    if (!isMobileDevice()) {
      if (isAuthenticated) {
        authService.reloadAuthToken();

        if (uiOnboardingLevel !== UI_VERSION) {
          showHomeOnboardingTour();
        }

        clientSocketRef.current = io.connect(
          process.env.REACT_APP_BE_HOST + ":" + process.env.REACT_APP_BE_PORT,
        );

        clientSocketRef.current.on("me", (id) => {
          setMe(id);
        });

        clientSocketRef.current.on("callAccepted", (signal) => {
          setCallAccepted(true);
          if (signal) {
            connectionRef.current.signal(signal);
          }
        });

        clientSocketRef.current.on("pair-by-qr", (pairingData) => {
          setViewersList((prev) => {
            if (!pairingData.isMultiThreadedStream) {
              return [pairingData.pin];
            } else {
              const MAX_USERS = 3;
              for (let i = 0; i < MAX_USERS; i++) {
                if (prev[i] === "" || i + 1 === MAX_USERS) {
                  const currentList = [...prev];
                  currentList[i] = pairingData.pin;
                  return currentList;
                }
              }
            }
          });
        });

        clientSocketRef.current.on("callDenied", () => {
          setApiResponse((existingResponses) => {
            return {
              ...existingResponses,
              streamDenied: "Pairing request has been denied by user!",
            };
          });
        });

        clientSocketRef.current.on("P2PErrorDetails", (errorDetails) => {
          alert(`P2P connection failure. \n ${errorDetails.code}`);
          console.log("P2P error details:", errorDetails);
          setCallAccepted(false);
          setStream(null);
        });

        clientSocketRef.current.on("connect_error", () => {
          alert(
            "Unable to establish connection to server.\nContact us at contact@head3dio.com if error persists.",
          );
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && subscriptionType === "none") {
      window.location.href = SUBSCRIPTION_PAGE;
    }
    if (isAuthenticated && isMobileDevice() && subscriptionType !== "none") {
      window.location.href = VIEWER_LANDING_PAGE;
    }
  }, [subscriptionType, isAuthenticated]);

  const establishVideoStreamUsingStreamClient = async () => {
    const videoStreamTrack = await getMyVideoStreamTrack().catch((err) => {
      console.log("Agora camera access error:", err);
    });
    setStream(videoStreamTrack);
  };

  const establishVideoStream = async () => {
    const mediaStreamOptions = {
      video: {
        frameRate: { ideal: 60 },
      },
      audio: false,
    };
    await navigator.mediaDevices
      .getDisplayMedia(mediaStreamOptions)
      .then((stream) => {
        setStream(stream);
      })
      .catch((err) => {
        alert("Error retrieving stream. Please check permissions");
        console.log("Stream error details:", err);
      });
  };

  const callUser = (id) => {
    const peer = new Peer({
      initiator: true,
      trickle: false,
      stream: stream,
    });

    peer.on("signal", (data) => {
      clientSocketRef.current.emit("req-session", {
        userToCall: id,
        signalData: data,
        from: me,
      });
    });

    peer.on("close", () => {
      setCallAccepted(false);
    });

    connectionRef.current = peer;
  };
  const diconnect = () => {
    console.log(stream.active);
    setCallAccepted(false);
    setStream(undefined);
    if (connectionRef.current) {
      connectionRef.current.destroy();
    }
    window.location.reload();
  };

  const sendPairingRequest = async (pairButtonRef) => {
    if (!stream) {
      setApiResponse({ streamErr: "No video stream found!" });
      return { result: false };
    }

    viewersList
      .filter((v) => v && v !== "")
      .forEach((viewerId) => {
        clientSocketRef.current.emit("stream-provider-info", {
          userToCall: viewerId,
          from: me,
          signal: {
            backgroundsEnabled:
              subscriptionType === "gold" || subscriptionType === "value",
            roomId: me,
            streamThreading: enableMultiThreadedStream
              ? "multi-threaded"
              : "single-threaded",
          },
        });
      });

    if (enableMultiThreadedStream) {
      createRTCClient(me, null, null)
        .then(async (client) => {
          await client.publish([stream]);
          pairButtonRef.current.style.cursor = "not-allowed";
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      callUser(viewersList[0]);
      pairButtonRef.current.style.cursor = "not-allowed";
    }
  };

  const establishStreamForm = (
    <div style={{ display: "inline-flex" }}>
      <h1 className="stream-status">Video Stream Not Found!</h1>
      <a
        id="establish-stream-btn"
        style={{ marginLeft: 20 }}
        className="buttonGreen"
        onClick={
          enableMultiThreadedStream
            ? establishVideoStreamUsingStreamClient
            : establishVideoStream
        }
      >
        Start
      </a>
    </div>
  );

  return isAuthenticated && subscriptionType !== "none" && !isMobileDevice() ? (
    <div>
      {stream ? (
        <h1 className="stream-status">
          Stream OK <i className="fas fa-stream"></i>
        </h1>
      ) : (
        establishStreamForm
      )}
      <br />
      {apiResponse.streamErr && (
        <Alert type="error" text={apiResponse.streamErr} />
      )}
      {apiResponse.streamDenied && (
        <Alert type="error" text={apiResponse.streamDenied} />
      )}
      {!callAccepted ? (
        <PairingForm
          viewersList={viewersList}
          setViewersList={setViewersList}
          sendPairingRequest={sendPairingRequest}
          enableMultiThreadedStream={enableMultiThreadedStream}
          setEnableMultiThreadedStream={setEnableMultiThreadedStream}
          setStream={setStream}
          subscriptionType={subscriptionType}
          me={me}
        />
      ) : (
        <>
          <h1>
            Viewer Connected <i className="fas fa-user-check"></i>
          </h1>
          <br />
          <a className="buttonRed" onClick={diconnect}>
            Disconnect
          </a>
          <div className="paddingtop20">
            <a href={INFORMATION_PAGE} target="_blank" rel="noreferrer">
              Need troubleshooting tips?
            </a>
          </div>
        </>
      )}
    </div>
  ) : (
    <Loading text={AUTH_CHECKING_MESSAGE} />
  );
}

export default Home;
